import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

import { useTheme, makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Alert from '@material-ui/lab/Alert';

import states from 'app/utils/States';

import useApi from 'app/context/useApi';
import { Typography } from '@material-ui/core';

const useFormStyles = makeStyles(theme => ({
    elementBase: {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: theme.typography.body1.fontWeight,
        fontFamily: theme.typography.body1.fontFamily,
        lineHeight: '1.1876em',
        letterSpacing: theme.typography.body1.letterSpacing,
        color: theme.palette.text.primary,
        color: 'rgba(0, 0, 0, 0.87)',
        cursor: 'text',
        padding: '18.5px 14px',
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderRadius: theme.shape.borderRadius,
        borderWidth: 2,
        borderStyle: 'solid',
        "&:hover": {
            borderColor: 'rgba(0, 0, 0, 0.87)'
        },
    },
    elementComplete: {
        borderColor: theme.palette.success.main + ' !important',
        borderWidth: 2,
    },
    elementEmpty: {

    },
    elementFocus: {
        borderColor: theme.palette.primary.main + ' !important',
        borderWidth: 2,
    },
    elementInvalid: {
        borderColor: theme.palette.error.main + ' !important',
        borderWidth: 2,
    },
    alert: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        width: '100%',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

const initValues = {
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    email: '',
    phone: ''
}

export default function SavePaymentMethodForm(props) {

    const stripe = useStripe();
    const elements = useElements();
    const api = useApi();
    const theme = useTheme();
    const classes = useFormStyles();

    const [ values, setValues ] = useState(initValues);
    const [ clientSecret, setClientSecret ] = useState(null);
    const [ method, setMethod ] = useState(1) // 1 = credit card, 2 = ACH
    const [ step, setStep ] = useState(1);

    const [ errors, setErrors ] = useState([]);
    const [ stripeLoaded, setStripeLoaded ] = useState(false);

    const { onSave, user, loading, setLoading } = props;

    const getIntent = () => {
        setLoading(true);

        const intentData = {
            user_id: user.id,
        }

        api.post('/stripe/intent/setup', intentData)
            .then((res) => setClientSecret(res.client_secret))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));

    }

    useEffect(() => {
        if (user) {
            setValues({
                name: user.first + " " + user.last,
                line1: user.address_one || "",
                line2: user.address_two || "",
                city: user.city || "",
                state: user.state || "",
                postal_code: user.zip || "",
                country: user.country || "US",
                email: user.email || '',
                phone: user.phone || ''
            })
            getIntent();
        } else {
            setValues(initValues);
        }
    }, [user]);

    useEffect(() => {
        setStripeLoaded((stripe && elements) ? true : false);
    }, [stripe, elements]);

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    const postCard = async () => {
        setLoading(true);
        setErrors({});

        if (! stripeLoaded) {
            setErrors({stripe: 'Unable to connect to our payment processor. Please try again later.'})
            return false;
        }

        const confirmCardData = {
            payment_method: {
                card: elements.getElement(CardElement),
                billing_details: {
                    name: values.name,
                    email: values.email,
                    address: {
                        line1: values.line1,
                        line2: values.line2,
                        city: values.city,
                        state: values.state,
                        postal_code: values.postal_code
                    }
                },
            }
        }

        // Then we are going to submit the card payment to stripe
        const result = await stripe.confirmCardSetup(clientSecret, confirmCardData);

        // If Error, let's display the error and update the payment on our servers
        if (result.error) {
            setLoading(false);
            setErrors({ stripe: result.error.message });
        } else {
            setErrors({});
            onSave();
        }
    }

    const verifyAccount = () => {

        setLoading(true);
        setErrors({});

        const er = {};

        if (values.name == '') er.name = 'A name is required to verify your account.';
        if (values.email == '') er.email = 'An email address is required to verify your account.';

        if (Object.keys(er).length > 0) {
            setLoading(false);
            setErrors(er);
            return;
        }

        stripe.collectBankAccountForSetup({
            clientSecret: clientSecret,
            params: {
                payment_method_type: 'us_bank_account',
                payment_method_data: {
                    billing_details: {
                        name: values.name,
                        email: values.email,
                    },
                },
            },
            expand: ['payment_method'],
        }).then(({setupIntent, error}) => {

            setLoading(false);

            if (error) {
                setErrors({ stripe: error.message });
                // PaymentMethod collection failed for some reason.
            } else if (setupIntent.status === 'requires_payment_method') {
                // Customer canceled the hosted verification modal. Present them with other
                // payment method type options.
                // No need to do anything as they will remain on the first page
            } else if (setupIntent.status === 'requires_confirmation') {
                // We collected an account - possibly instantly verified, but possibly
                // manually-entered. Display payment method details and mandate text
                // to the customer and confirm the intent once they accept
                // the mandate.
                setStep(2);
            } else {
                setErrors({ stripe: 'Something went wrong. Please try again.' });
            }
          });
    }

    const authorizeAccount = () => {

        setLoading(true);
        setErrors({});

        stripe.confirmUsBankAccountSetup(clientSecret).then(({setupIntent, error}) => {

            setLoading(false);

            if (error) {
                console.error(error.message);
                // The payment failed for some reason.
                setErrors({ stripe: error.message });
            } else if (setupIntent.status === "requires_payment_method") {
                // Confirmation failed. Attempt again with a different payment method.
                setStep(1);
                setErrors({ stripe: 'Account Confirmation Failed. Please try another account or another payment method.' });
            } else if (setupIntent.status === "succeeded") {
                // Confirmation succeeded! The account is now saved.
                // Display a message to customer.
                setStep(3);
            } else if (setupIntent.next_action?.type === "verify_with_microdeposits") {
                // The account needs to be verified via microdeposits.
                // Display a message to consumer with next steps (consumer waits for
                // microdeposits, then enters a statement descriptor code on a page sent to them via email).
                api.post('/stripe/microdeposits/email', setupIntent.next_action.verify_with_microdeposits);
                setStep(4)
            } else {
                setStep(1);
                setErrors({ stripe: 'Could not Authorize Account. Please try again.' });
            }
        });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const handleCardError = event => {
        if (event.error) {
            setErrors({...errors, stripe: event.error.message })
        } else {
            setErrors({...errors, stripe: undefined });
        }
    }

    return (
        <form>
            {(step === 1) &&
                <div>
                    <Typography variant="subtitle1" className={classes.marginBottom}>
                        Please enter your payment information below.
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                id="input-typr"
                                label="Payment Method"
                                value={method}
                                onChange={e => setMethod(e.target.value)}
                                fullWidth
                                error={isError('name')}
                                helperText={getErrorMessage('name')}
                                variant="outlined"
                                select
                                >
                                <MenuItem key={1} value={1}>
                                    Credit Card
                                </MenuItem>
                                <MenuItem key={2} value={2}>
                                    Bank Account (ACH)
                                </MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="input-name"
                                label="Account Holder Name"
                                value={values.name}
                                onChange={handleChange('name')}
                                fullWidth
                                error={isError('name')}
                                helperText={getErrorMessage('name')}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-address-one"
                                label="Address One"
                                value={values.line1}
                                onChange={handleChange('line1')}
                                fullWidth
                                error={isError('line1')}
                                helperText={getErrorMessage('line1')}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-address-two"
                                label="Address Two"
                                value={values.line2}
                                onChange={handleChange('line2')}
                                fullWidth
                                error={isError('line2')}
                                helperText={getErrorMessage('line2')}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="input-city"
                                label="City"
                                fullWidth
                                value={values.city}
                                onChange={handleChange('city')}
                                error={isError('city')}
                                helperText={getErrorMessage('city')}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                id="input-state"
                                select
                                fullWidth
                                label="State"
                                value={values.state}
                                onChange={handleChange('state')}
                                error={isError('state')}
                                helperText={getErrorMessage('state')}
                                variant="outlined"
                                >
                                {states.map( state => (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >
                                        {state.abbreviation}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="input-zip"
                                label="Zip"
                                fullWidth
                                value={values.postal_code}
                                onChange={handleChange('postal_code')}
                                error={isError('postal_code')}
                                helperText={getErrorMessage('postal_code')}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {(method === 1) &&
                                <CardElement
                                    onChange={handleCardError}
                                    options={{
                                        hidePostalCode: true,
                                        classes: {
                                            base: classes.elementBase,
                                            complete: classes.elementComplete,
                                            empty: classes.elementEmpty,
                                            focus: classes.elementFocus,
                                            invalid: classes.elementInvalid
                                        },
                                        style: {
                                            base: {
                                                iconColor: theme.palette.primary.dark,
                                            },
                                        },
                                    }}
                                />
                            }
                            {(method === 2) &&
                                <TextField
                                    id="input-email"
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    value={values.email}
                                    onChange={handleChange('email')}
                                    error={isError('email')}
                                    helperText={getErrorMessage('email')}
                                    variant="outlined"
                                />
                            }
                        </Grid>
                        {isError('stripe') &&
                            <Grid item xs={12}>
                                <Alert variant="filled" severity="error" className={classes.alert}>
                                    {getErrorMessage('stripe')}
                                </Alert>
                            </Grid>
                        }
                    </Grid>
                    {method === 1 &&
                        <Button
                            onClick={postCard}
                            color="primary"
                            disabled={!stripeLoaded || loading}
                            variant="contained"
                            className={classes.saveButton}
                            >
                            Save Card
                        </Button>
                    }
                    {method === 2 &&
                        <Button
                            onClick={verifyAccount}
                            color="primary"
                            disabled={!stripeLoaded || loading}
                            variant="contained"
                            className={classes.saveButton}
                            >
                            Connect Account
                        </Button>
                    }
                </div>
            }
            {(step === 2) &&
                <div>
                    <Typography variant="subtitle1" className={classes.marginBottom}>
                        Please review the following mandate...
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        By clicking <strong>Agree to Mandate</strong>, you authorize Club Apps, llc to debit the bank account specified above for any amount owed for charges arising from your use of Club Apps services, pursuant to Club Apps’ website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to Club Apps with 30 (thirty) days notice.
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        If you use Club Apps’ services periodically pursuant to Club Apps’ terms, you authorize Club Apps to debit your bank account periodically. Payments that fall outside of the regular debits authorized above will only be debited after your authorization is obtained.
                    </Typography>
                    {isError('stripe') &&
                        <Alert variant="filled" severity="error" className={classes.alert}>
                            {getErrorMessage('stripe')}
                        </Alert>
                    }
                    <Button
                        onClick={authorizeAccount}
                        color="primary"
                        disabled={!stripeLoaded || loading}
                        variant="contained"
                        className={classes.saveButton}
                        >
                        Agree to Mandate
                    </Button>
                </div>
            }
            {(step === 3) &&
                <div>
                    <Typography variant="h6" color="success" gutterBottom align="center">
                        Account Connected Successfully
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom >
                        Your bank account has been successfully connected. You can now use your bank account for services through our application. To set this account as your default payment method, click on the account once this dialog has closed and then click <strong>MAKE DEFAULT</strong> on the bottom left of the dialogue.
                    </Typography>
                    <Button
                        onClick={onSave}
                        color="primary"
                        disabled={loading}
                        variant="outlined"
                        className={classes.saveButton}
                        >
                        Close
                    </Button>
                </div>
            }
            {(step === 4) &&
                <div>
                    <Typography variant="h6" color="success" gutterBottom align="center">
                        Account Pending Deposits
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom >
                        Your account requires further verification. We have deposited either one or two small amounts in to your bank account to verify ownership. You should be receiving an email from Club Apps shortly with further instructions. Typically it takes 1 to 2 business days for these deposits to show up in your account. Once verified, your bank account will show up here on the payments page.
                    </Typography>
                    <Button
                        onClick={onSave}
                        color="primary"
                        disabled={loading}
                        variant="outlined"
                        className={classes.saveButton}
                        >
                        Close
                    </Button>
                </div>
            }
        </form>
    )
}
